import { Outlet, NavLink, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  FolderKanban, 
  ListTodo, 
  Layers, 
  Menu,
  X,
  Settings,
  ChevronDown,
  ChevronRight,
  Users,
  LogOut,
  Mail,
  ScrollText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import LoginButton from "@/components/LoginButton";
import UserMenu from "@/components/UserMenu";

const baseNavItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { 
    path: "/projects", 
    label: "Projects", 
    icon: FolderKanban,
    children: [
      { path: "/tasks", label: "Tasks", icon: ListTodo },
      { path: "/task-groups", label: "Task Groups", icon: Layers },
    ]
  },
];

const adminNavItems = [
  { path: "/team", label: "Team", icon: Users },
  { path: "/reports", label: "Email Reports", icon: Mail },
  { path: "/audit-log", label: "Audit Trail", icon: ScrollText },
  { path: "/settings", label: "Settings", icon: Settings },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({ "/projects": true });
  const location = useLocation();
  const { isAuthenticated, user, loading, logout, canEdit } = useAuth();
  
  // Debug: log when navItems are determined
  console.log("Layout render - canEdit:", canEdit, "user?.is_management:", user?.is_management, "user?.role:", user?.role);
  
  // Only show Team and Settings for management/admin users
  const navItems = canEdit ? [...baseNavItems, ...adminNavItems] : baseNavItems;

  const toggleExpanded = (path) => {
    setExpandedItems(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const renderNavItem = (item, isChild = false) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.path];
    const isChildActive = hasChildren && item.children.some(child => location.pathname === child.path);

    return (
      <div key={item.path}>
        <div className="flex items-center">
          {isChild && (
            <span className="text-muted-foreground ml-2 mr-1 text-xs">└</span>
          )}
          <NavLink
            to={item.path}
            onClick={(e) => {
              if (hasChildren) {
                // Navigate to the page AND toggle expansion
                toggleExpanded(item.path);
              }
              setSidebarOpen(false);
            }}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            className={cn(
              "flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
              isActive || isChildActive
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
              isChild && "py-2"
            )}
          >
            <Icon className={cn("w-5 h-5", isChild && "w-4 h-4")} />
            <span className="flex-1">{item.label}</span>
            {hasChildren && (
              <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleExpanded(item.path); }}>
                {isExpanded 
                  ? <ChevronDown className="w-4 h-4" />
                  : <ChevronRight className="w-4 h-4" />
                }
              </span>
            )}
          </NavLink>
        </div>
        
        {/* Render children if expanded */}
        {hasChildren && isExpanded && (
          <div className="ml-4 mt-1 space-y-1">
            {item.children.map(child => renderNavItem(child, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Gamer Packaging branded accent bar */}
      <div className="gamer-accent-bar fixed top-0 left-0 right-0 z-[60]" />
      
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-1 left-0 right-0 z-50 h-16 border-b bg-card/80 backdrop-blur-md flex items-center justify-between px-4">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <img src="/images/gamer-logo.jpg" alt="Gamer Packaging" className="ml-2 h-8" />
        </div>
        <div>
          {!loading && (
            isAuthenticated ? <UserMenu /> : <LoginButton size="sm" />
          )}
        </div>
      </header>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-1 left-0 z-50 h-[calc(100%-4px)] w-64 bg-card border-r transform transition-transform duration-300 ease-in-out lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center px-4 border-b">
            <img src="/images/gamer-logo.jpg" alt="Gamer Packaging" className="h-10" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1">
            {navItems.map((item) => renderNavItem(item))}
          </nav>

          {/* Footer - User Section */}
          <div className="px-4 py-4 border-t">
            {loading ? (
              <div className="flex items-center gap-3 px-2">
                <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded animate-pulse w-20" />
                  <div className="h-3 bg-muted rounded animate-pulse w-16" />
                </div>
              </div>
            ) : isAuthenticated && user ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 px-2">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
                    {(user.displayName || user.display_name || "U").substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">
                      {user.displayName || user.display_name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate" data-testid="sidebar-user-email">
                      {user.email}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full" 
                  onClick={logout}
                  data-testid="sidebar-logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            ) : (
              <div className="px-2">
                <LoginButton variant="outline" className="w-full" />
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-[68px] lg:pt-1">
        <div className="p-6 md:p-8 lg:p-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
